import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ArrowUpRight, Check, Zap, ShieldCheck,
  KeyRound, ShieldAlert, Radar, BatteryCharging, MonitorDot, Cctv,
} from 'lucide-react';
import { SECURITY_PAGE, CONTACT } from '../mock';
import Navbar from './Navbar';
import SiteFooter from './SiteFooter';

const ICONS = { KeyRound, ShieldAlert, Radar, BatteryCharging, MonitorDot, Cctv };

export default function ElectronicSecurity() {
  const { hero, services, process } = SECURITY_PAGE;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Electronic Security — Electronico Holdings';
  }, []);

  return (
    <div className="relative min-h-screen bg-[#0a1526] text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-36 pb-20 px-5 overflow-hidden grid-bg">
        <div className="blob bg-[#0ef3fe] w-[460px] h-[460px] -top-24 -left-24 animate-pulse-glow" />
        <div className="blob bg-[#0a6bd6] w-[360px] h-[360px] top-20 right-0 opacity-40" />
        <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full neon-border px-4 py-1.5 mb-6">
              <ShieldCheck className="h-4 w-4 text-[#0ef3fe]" />
              <span className="font-mono-accent text-xs text-white/80">{hero.kicker}</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.08] mb-6">
              Security, <span className="gradient-text animate-gradient">Engineered</span> for a Safer Tomorrow
            </h1>
            <p className="text-lg text-white/65 max-w-xl mb-8 leading-relaxed">{hero.subtitle}</p>
            <div className="flex flex-wrap gap-4">
              <a href="#services" className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#0ef3fe] to-[#0a6bd6] text-black px-7 py-3.5 font-semibold hover:shadow-[0_0_30px_rgba(14,243,254,0.5)] transition-shadow">
                Explore Capabilities <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="#security-cta" className="inline-flex items-center gap-2 rounded-xl neon-border px-7 py-3.5 font-semibold text-white hover:bg-white/5 transition-colors">
                Request a Survey
              </a>
            </div>
            <div className="grid grid-cols-4 gap-4 mt-12 max-w-lg">
              {hero.stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-2xl md:text-3xl font-bold text-glow-cyan">{s.value}</div>
                  <div className="text-xs text-white/50 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative animate-floaty">
            <div className="absolute -inset-4 bg-gradient-to-tr from-[#0ef3fe]/30 to-[#0a6bd6]/30 blur-2xl rounded-3xl" />
            <div className="relative rounded-3xl overflow-hidden neon-border">
              <img src={hero.image} alt="Electronic security" className="w-full h-[440px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1526] via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <span className="font-mono-accent text-xs text-white">// SECURE PERIMETER</span>
                <span className="flex items-center gap-2 text-xs text-white/70">
                  <span className="h-2 w-2 rounded-full bg-[#5bd0ff] animate-pulse-glow" /> Armed
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="relative py-20 px-5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono-accent text-xs text-[#0ef3fe]">// CAPABILITIES</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Six Ways We Protect You</h2>
            <p className="text-white/55 mt-4 max-w-xl mx-auto">A complete, integrated security stack — designed, deployed and supported end to end.</p>
          </div>

          <div className="space-y-8">
            {services.map((s, i) => {
              const Icon = ICONS[s.icon];
              const flip = i % 2 === 1;
              return (
                <div
                  key={s.id}
                  id={s.id}
                  className="group grid lg:grid-cols-2 gap-8 items-center neon-border rounded-3xl p-6 md:p-8 hover:border-[#0ef3fe]/50 hover:shadow-[0_0_40px_rgba(14,243,254,0.18)] transition-all duration-300 scroll-mt-28"
                >
                  <div className={`relative h-64 md:h-80 rounded-2xl overflow-hidden ${flip ? 'lg:order-2' : ''}`}>
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d16]/70 to-transparent" />
                    <span className="absolute top-4 left-4 font-display text-5xl font-bold text-white/20">{s.no}</span>
                  </div>
                  <div className={flip ? 'lg:order-1' : ''}>
                    <div className="h-14 w-14 rounded-2xl bg-[#0ef3fe]/10 flex items-center justify-center mb-5 group-hover:glow-cyan transition-shadow">
                      <Icon className="h-7 w-7 text-[#0ef3fe]" />
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl font-bold mb-3">{s.title}</h3>
                    <p className="text-white/60 leading-relaxed mb-6">{s.desc}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {s.points.map((p) => (
                        <div key={p} className="flex items-center gap-2.5 text-sm text-white/80">
                          <span className="flex h-5 w-5 items-center justify-center rounded-md bg-[#0ef3fe]/15 shrink-0">
                            <Check className="h-3.5 w-3.5 text-[#0ef3fe]" />
                          </span>
                          {p}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative py-20 px-5 grid-bg">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono-accent text-xs text-[#0a6bd6]">// HOW WE WORK</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">From Risk to Resilience</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p) => (
              <div key={p.step} className="neon-border rounded-3xl p-7 hover:-translate-y-2 transition-transform duration-300">
                <div className="font-mono-accent text-5xl font-bold gradient-text mb-4">{p.step}</div>
                <h3 className="font-display text-lg font-bold mb-2">{p.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="security-cta" className="relative py-20 px-5">
        <div className="max-w-5xl mx-auto">
          <div className="relative neon-border rounded-3xl p-10 md:p-16 overflow-hidden text-center">
            <div className="blob bg-[#0ef3fe] w-80 h-80 -top-20 -left-10 opacity-30" />
            <div className="blob bg-[#0a6bd6] w-80 h-80 -bottom-20 -right-10 opacity-30" />
            <div className="relative z-10">
              <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">Ready to Secure Your Site?</h2>
              <p className="text-white/60 max-w-xl mx-auto mb-8">
                Book a free on-site risk assessment and our engineers will design a security stack tailored to your assets.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href={`mailto:${CONTACT.email}`} className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#0ef3fe] to-[#0a6bd6] text-black px-7 py-3.5 font-semibold hover:shadow-[0_0_30px_rgba(14,243,254,0.5)] transition-shadow">
                  Request a Survey <ArrowUpRight className="h-5 w-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
                <Link to="/" className="inline-flex items-center gap-2 rounded-xl neon-border px-7 py-3.5 font-semibold text-white hover:bg-white/5 transition-colors">
                  <ArrowLeft className="h-4 w-4" /> All Divisions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter />
    </div>
  );
}
